import { useState, type FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import {
  Badge,
  Button,
  Card,
  Input,
  MagnusOperaIcon,
  ThemeToggle,
} from "@insights/ui";
import { authClient } from "../../../api";

type LoginPageProps = {
  returnTo?: string;
  invitation?: boolean;
  initialEmail?: string;
  initialSignup?: boolean;
};

export function LoginPage({
  returnTo = "/",
  invitation = false,
  initialEmail = "",
  initialSignup,
}: LoginPageProps) {
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [signup, setSignup] = useState(initialSignup ?? invitation);
  const [error, setError] = useState("");
  const verificationPending =
    new URLSearchParams(routeLocation.search).get("verification") === "pending";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    const result = signup
      ? await authClient.signUp.email({
          email,
          password,
          name,
          callbackURL: new URL(returnTo, window.location.origin).href,
        })
      : await authClient.signIn.email({ email, password });
    if (result.error) {
      setError(result.error.message ?? "Unable to sign in");
      return;
    }
    if (signup) {
      const pending = new URL(returnTo, window.location.origin);
      pending.searchParams.set("verification", "pending");
      navigate(`${pending.pathname}${pending.search}`, { replace: true });
      return;
    }
    navigate(returnTo);
  }

  if (verificationPending) {
    return (
      <main className="auth-shell">
        <ThemeToggle className="auth-theme" />
        <section className="auth-copy">
          <Badge>Email verification</Badge>
          <h1>
            One more
            <br />
            <em>small step.</em>
          </h1>
          <p>Verify your email before joining the workspace.</p>
        </section>
        <Card className="auth-card">
          <div className="mark">
            <MagnusOperaIcon /> Insights
          </div>
          <h2>Check your email</h2>
          <p>
            We sent a verification link
            {email && (
              <>
                {" "}
                to <strong>{email}</strong>
              </>
            )}
            . Open it in this browser to return to the invitation automatically.
          </p>
          <button
            className="text-button"
            onClick={() => {
              setSignup(false);
              navigate(returnTo, { replace: true });
            }}
          >
            Already verified? Sign in
          </button>
        </Card>
      </main>
    );
  }

  return (
    <main className="auth-shell">
      <ThemeToggle className="auth-theme" />
      <section className="auth-copy">
        {invitation && <Badge>Workspace invitation</Badge>}
        <h1>
          Build intelligence,
          <br />
          <em>without the noise.</em>
        </h1>
        {invitation && (
          <p>Create an account or sign in to join the workspace.</p>
        )}
        <div className="signal">
          <span />
          <span />
          <span />
          <span />
        </div>
      </section>
      <Card className="auth-card">
        <div className="mark">
          <MagnusOperaIcon /> Insights
        </div>
        <h2>{signup ? "Create your account" : "Welcome back"}</h2>
        <p>
          {signup
            ? invitation
              ? `Create the account invited as ${initialEmail}.`
              : "Start an independent Insights workspace."
            : invitation
              ? `Sign in as ${initialEmail} to accept the invitation.`
              : "Sign in to inspect your engineering system."}
        </p>
        <form onSubmit={submit}>
          {signup && (
            <label>
              Name
              <Input
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
              />
            </label>
          )}
          <label>
            Email
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              readOnly={invitation}
              required
            />
          </label>
          <label>
            Password
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              minLength={8}
              required
            />
          </label>
          {!signup && !invitation && (
            <Link
              className="auth-text-link auth-forgot-password"
              to="/forgot-password"
            >
              Forgot password?
            </Link>
          )}
          {error && <div className="error">{error}</div>}
          <Button type="submit">{signup ? "Create account" : "Sign in"}</Button>
        </form>
        {!invitation && (
          <button
            className="text-button"
            onClick={() => {
              setSignup(!signup);
              setError("");
            }}
          >
            {signup
              ? "Already registered? Sign in"
              : "New here? Create an account"}
          </button>
        )}
      </Card>
    </main>
  );
}
